import React, { Fragment, useRef, useState } from "react";
import { RCCButton, RCCGrid, RCCHookToast, RCCInput, RCCServicesApi, RCCSwitch, RCCTooltip } from "react-common-components";
import { useForm } from "react-hook-form";
import Title from "src/components/Title";
import { useAuth } from "src/context/Auth/context";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import { v4 } from "uuid";
import LovGrouperActions from "src/components/Lov/GrouperActions";
import LovGrouperIndicators from "src/components/Lov/GrouperIndicators";
import LovGrouperRisks from "src/components/Lov/GrouperRisks";
import LovGrouperOccurrences from "src/components/Lov/GrouperOccurrences";
import LovGrouperOKRs from "src/components/Lov/GrouperOKRs";
import LovBusinessGroups from "src/components/Lov/BusinessGroups";
import LovHomePage from "src/components/Lov/HomePage";
import LovGrouperMeeting from "src/components/Lov/GrouperMeetings";
import {IEftba001, IEftba002, IEftba004, IFormUsersProfile, IRefModalsProfile} from "./interface";
import { schemaUserProfile } from "./schemas";
import { FormStyled } from "./styles";
import ModalsProfile from "./modals";

const FormProfile: React.FC<IFormUsersProfile> = ({
	data
}) => {
	const {translate} = useTranslation();
	const {addToast} = RCCHookToast();
	const { businessGroup, updateUserNameAndTreatment, updateHomePage } = useAuth();
	const {handleSubmit, register, setError, clearErrors, formState: {errors}, control} = useForm();
    
	const modalsProfileRef = useRef<IRefModalsProfile>(null);

	const [buttonLoading, setButtonLoading] = useState(false);
    
	const onSubmit = async (dt: any) => {
		try{
			clearErrors();         
			setButtonLoading(true);
			await schemaUserProfile(translate).validate(dt, {
				abortEarly: false
			});

			await RCCServicesApi.putRequest({
				method: "User/Change",
				body: {
					...dt,
					efa005NotificaEmail: dt.efa005NotificaEmail ? "S" : "N",
					efa005Id: data.efa005Id,
					efa005Efa001Id: dt.efa005Efa001Id && dt.efa005Efa001Id > 0 ? dt.efa005Efa001Id : null,
					eftba105: {
						...dt.eftba105,
						efa105Id: data.eftba105.efa105Id
					}
				}
			});

			addToast({
				title: translate("Sucesso"),
				description: translate("Informações alteradas com sucesso"),
				color: "green"
			});

			updateUserNameAndTreatment(dt.efa005Nome, dt.efa005Tratamento);
			updateHomePage(dt.efa005Paginicial);
			
			setButtonLoading(false);
		}catch(err){
			setButtonLoading(false);
			if(getValidationErrors(err, setError))
				return;

			standardErrorMessages(err, translate("Erro ao alterar usuário"), addToast);
		}	
	};

	return (
		<>
			<FormStyled onSubmit={handleSubmit(onSubmit)}>
				<RCCGrid container item alignItems="center" justifyContent="center">
					<h3>{data.efa005Email}</h3>
					<RCCTooltip color="grey" text={translate("Alterar senha")}>
						<RCCButton
							color="grey"
							size="md"
							layout="isRounded"
							icon={{
								name: "key"
							}}
							onClick={() => modalsProfileRef.current?.openModalChangePassword()}
						/>
					</RCCTooltip>
					<RCCTooltip color="grey" text={translate("Autenticação em dois fatores")}>
						<RCCButton
							color="grey"
							size="md"
							layout="isRounded"
							icon={{
								name: "shield"
							}}
							onClick={() => modalsProfileRef.current?.openModalTFA()}
						/>
					</RCCTooltip>
				</RCCGrid>
				<RCCGrid container item  >
					<RCCInput 
						name="efa005Nome"
						register={register}
						label={translate("Nome completo")}
						maxLength={60}
						required
						defaultValue={data.efa005Nome}
						error={errors.efa005Nome}
					/>
				</RCCGrid>
				<RCCGrid container item >
					<RCCInput 
						name="efa005Tratamento"
						register={register}
						label={translate("Tratamento")}
						maxLength={40}
						defaultValue={data.efa005Tratamento !== null ? data.efa005Tratamento.toString() : undefined}
					/>
				</RCCGrid>
				<RCCGrid container item >
					<LovHomePage 
						name="efa005Paginicial"
						control={control}
						defaultValue={data.efa005Paginicial !== null ? data.efa005Paginicial.toString() : "1"}
					/>
				</RCCGrid>
				<RCCGrid container item>
					<LovBusinessGroups 
						name="efa005Efa001Id"
						label={translate("Definir grupo empresa padrão")}
						defaultValue={data.efa005Efa001Id.toString()}
						control={control}
					/>

				</RCCGrid>

				<RCCGrid container item>
					<RCCSwitch
						name="efa005NotificaEmail"
						register={register}
						label={translate("Receber as notificações por email")}
						defaultChecked={data.efa005NotificaEmail === "S"}
						labelPosition="end"
					/>
				</RCCGrid>

				<hr />
				<RCCGrid container item >
					<Title color="purple">{translate("Agrupadores")} {translate("de")} {businessGroup.efa001Fantasia} :</Title>
				</RCCGrid>
				
				<LovGrouperActions 
					control={control}
					name="eftba105.efa105Painelacoes"
					label={translate("Ações")}
					defaultValue={data.eftba105.efa105Painelacoes !== null ? data.eftba105.efa105Painelacoes.toString() : "1"}
				/>

				<LovGrouperIndicators
					control={control}
					name="eftba105.efa105Painelindicadores"
					label={translate("Indicadores")}
					defaultValue={data.eftba105.efa105Painelindicadores !== null ? data.eftba105.efa105Painelindicadores.toString() : "1"}
				/>

				<LovGrouperRisks
					control={control}
					name="eftba105.efa105Painelriscos"
					label={translate("Riscos")}
					defaultValue={data.eftba105.efa105Painelriscos !== null ? data.eftba105.efa105Painelriscos.toString() : "1"}
				/>

				<LovGrouperOccurrences
					control={control}
					name="eftba105.efa105Painelocorrencias"
					label={translate("Ocorrências")}
					defaultValue={data.eftba105.efa105Painelocorrencias !== null ? data.eftba105.efa105Painelocorrencias.toString() : "1"}
				/>

				<LovGrouperOKRs
					control={control}
					name="eftba105.efa105Painelokr"
					label={"OKR's"}
					defaultValue={data.eftba105.efa105Painelokr !== null ? data.eftba105.efa105Painelokr.toString() : "1"}
				/>
				
				<LovGrouperMeeting  
					control={control}
					name="eftba105.efa105Painelreunioes"
					label={translate("Reuniões")}
					defaultValue={data.eftba105.efa105Painelreunioes !== null ? data.eftba105.efa105Painelreunioes.toString() : "1"}
				/>
				
				{data.eftba001.length > 0 && data.eftba001.map((efa001: IEftba001) => (
					<Fragment key={v4()}>
						<br />
						<RCCGrid container item >
							<Title color="purple">{translate("Grupo empresarial")}: {efa001.efa001Fantasia} </Title>
						</RCCGrid>
						<hr />
						<RCCGrid container item >
							<Title color="purple">{translate("Empresas")}:</Title>
						</RCCGrid>
						{efa001.eftba002.length > 0 && efa001.eftba002.map((efa002: IEftba002) => (<RCCGrid container item key={v4()}><Title color="grey" >{efa002.efa002Fantasia}</Title> </RCCGrid>))}

						<RCCGrid container item >
							<Title color="purple">{translate("Grupos de usuário")}:</Title>
						</RCCGrid>
						{efa001.eftba004.length > 0 && efa001.eftba004.map((efa004: IEftba004) => (<RCCGrid container item key={v4()}><Title color="grey" >{efa004.efa004Descricao}</Title> </RCCGrid>))}
					</Fragment>
				))}

				<RCCGrid container item  justifyContent="flex-end">
					<RCCButton 
						color="green" 
						type="submit"
						loading={buttonLoading}
						loadingText={translate("Carregando")}
						size="md"
					>{translate("Confirmar")}</RCCButton>
				</RCCGrid>
			</FormStyled>
			<ModalsProfile 
				ref={modalsProfileRef}
			/>
		</>
	);
};

export default FormProfile;
